import React from 'react';
import { Box, Typography, Button, Stack, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import CloudyBackground from './CloudyBackground';
// Vite destekli asset importları
// @ts-ignore - jpeg modül tipi için
import lightBg from '../photos/light_arkaplan.jpeg';
// @ts-ignore - jpeg modül tipi için
import darkBg from '../photos/dark_arkaplan.jpeg';
// @ts-ignore - jpg modül tipi için
import profileImg from '../photos/pp akşam.jpg';
import { 
  fadeInUpVariants, 
  staggerContainerVariants, 
  staggerItemVariants,
  buttonHoverVariants,
  textRevealVariants,
  floatingVariants
} from '../utils/animations';

const HeroContainer = styled(Box)(({ theme }) => ({
  minHeight: '88vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(180deg, #0f1220 0%, #111422 100%)'
    : 'linear-gradient(180deg, #e8f4ff 0%, #ffffff 100%)',
  transition: 'background 220ms ease',
  overflow: 'hidden',
  // leave space for floating header
  paddingTop: '96px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(60% 40% at 50% 30%, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.25) 40%, rgba(255,255,255,0) 70%)',
    filter: 'blur(30px)',
    opacity: 0.35,
    zIndex: 0
  }
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  maxWidth: '980px',
  padding: theme.spacing(4),
  marginInline: 'auto',
}));

const GlassCard = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.5) 100%)',
  backdropFilter: 'blur(16px) saturate(140%)',
  WebkitBackdropFilter: 'blur(16px) saturate(140%)',
  borderRadius: '28px',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.12)'
    : '1px solid rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(6),
  boxShadow: theme.palette.mode === 'dark'
    ? '0 30px 80px rgba(0, 0, 0, 0.6)'
    : '0 30px 80px rgba(0, 102, 204, 0.15)',
  // shrink the blur card so sides of scenery stay visible
  width: 'min(100%, 840px)',
  marginInline: 'auto',
  marginTop: theme.spacing(5),
  marginBottom: theme.spacing(5),
}));

const AnimatedButton = styled(Button)(() => ({
  borderRadius: '50px',
  padding: '12px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0, 102, 204, 0.3)'
  }
}));

const SocialButton = styled(Button)(() => ({
  minWidth: '50px',
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)'
  }
}));

const HeroSection: React.FC = () => {
  return (
    <HeroContainer>
      <CloudyBackground parallax={false} imageUrlLight={lightBg} imageUrlDark={darkBg} />
      
      <ContentWrapper>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainerVariants}
        >
          <motion.div variants={fadeInUpVariants}>
            <GlassCard>
              {/* Animated Avatar */}
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              >
                <Avatar
                  src={profileImg}
                  alt="Profile"
                  sx={{
                    width: 120,
                    height: 120,
                    margin: '0 auto 2rem',
                    border: '4px solid',
                    borderColor: 'primary.main',
                    cursor: 'default'
                  }}
                />
              </motion.div>
              
              {/* Animated Title */}
              <motion.div variants={textRevealVariants} custom={0}>
                <Typography variant="h1" gutterBottom sx={{ 
                  color: 'text.primary',
                  mb: 2
                }}>
                  <motion.span
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                  >
                    Mert Dalkır
                  </motion.span>
                </Typography>
              </motion.div>
              
              {/* Animated Subtitle */}
              <motion.div variants={textRevealVariants} custom={1}>
                <Typography variant="h4" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                  <motion.span
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                  >
                    AI & Web3 Developer
                  </motion.span>
                </Typography>
              </motion.div>
              
              {/* Animated Description */}
              <motion.div variants={textRevealVariants} custom={2}>
                <Typography variant="body1" color="text.primary" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.9 }}
                  >
                    Üniversite öğrencisi olarak AI ve Web3 teknolojileri üzerine çalışıyorum. 
                    Modern web uygulamaları geliştiriyor ve blockchain teknolojileri ile deneyimler yapıyorum.
                  </motion.span>
                </Typography>
              </motion.div>
              
              {/* Animated Buttons */}
              <motion.div 
                variants={staggerItemVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: 1.2 }}
              >
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4 }}>
                  <motion.div
                    variants={buttonHoverVariants}
                    initial="initial"
                    whileHover="hover"
                    whileTap="tap"
                  >
                    <AnimatedButton variant="contained" color="primary" size="large">
                      Projelerimi Gör
                    </AnimatedButton>
                  </motion.div>
                  <motion.div
                    variants={buttonHoverVariants}
                    initial="initial"
                    whileHover="hover"
                    whileTap="tap"
                  >
                    <AnimatedButton variant="outlined" color="primary" size="large">
                      İletişime Geç
                    </AnimatedButton>
                  </motion.div>
                </Stack>
              </motion.div>
              
              {/* Animated Social Buttons */}
              <motion.div
                variants={staggerContainerVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: 1.5 }}
              >
                <Stack direction="row" spacing={2} justifyContent="center">
                  {[
                    { icon: <GitHubIcon />, delay: 0 },
                    { icon: <LinkedInIcon />, delay: 0.1 },
                    { icon: <EmailIcon />, delay: 0.2 }
                  ].map((social, index) => (
                    <motion.div
                      key={index}
                      variants={staggerItemVariants}
                      whileHover={{ 
                        scale: 1.2, 
                        rotate: 10,
                        boxShadow: "0 8px 25px rgba(0, 102, 204, 0.3)"
                      }}
                      whileTap={{ scale: 0.9 }}
                      transition={{ 
                        type: "spring", 
                        stiffness: 400, 
                        damping: 10 
                      }}
                    >
                      <SocialButton variant="outlined" color="primary">
                        {social.icon}
                      </SocialButton>
                    </motion.div>
                  ))}
                </Stack>
              </motion.div>

              {/* Floating Decorative Elements */}
              <motion.div
                variants={floatingVariants}
                animate="animate"
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  width: '50px',
                  height: '50px',
                  background: 'linear-gradient(45deg, #0066cc, #00d4aa)',
                  borderRadius: '50%',
                  opacity: 0.1,
                  zIndex: -1
                }}
              />
              
              <motion.div
                variants={floatingVariants}
                animate="animate"
                transition={{ delay: 1, duration: 5 }}
                style={{
                  position: 'absolute',
                  bottom: '20px',
                  left: '20px',
                  width: '30px',
                  height: '30px',
                  background: 'linear-gradient(45deg, #00d4aa, #667eea)',
                  borderRadius: '30%',
                  opacity: 0.1,
                  zIndex: -1
                }}
              />
            </GlassCard>
          </motion.div>
        </motion.div>
      </ContentWrapper>
    </HeroContainer>
  );
};

export default HeroSection;